import type { CommissionType } from "@/lib/commission-catalog.api";
import type { CommissionTransaction } from "@/lib/commission.api";
import type { Party } from "@/lib/party.api";
import { getCommissionAmount } from "./commission-formatters";

export interface CommissionMetrics {
  total: number;
  pendingEdits: number;
  activeTypes: number;
  linkedParties: number;
}

export function getTypeLabel(types: CommissionType[], code: string): string {
  return types.find((type) => type.code === code)?.label ?? code;
}

export function getCommissionMetrics(commissions: CommissionTransaction[]): CommissionMetrics {
  return {
    total: commissions.reduce((sum, commission) => sum + getCommissionAmount(commission), 0),
    pendingEdits: commissions.filter((commission) => commission.pendingEditRequestId).length,
    activeTypes: new Set(commissions.map((commission) => commission.type)).size,
    linkedParties: new Set(
      commissions
        .flatMap((commission) => [commission.clientPartyId, commission.referrerPartyId])
        .filter(Boolean),
    ).size,
  };
}

export function getPartyName(
  parties: Record<number, Party>,
  partyId?: number | null,
): string | undefined {
  return partyId ? parties[partyId]?.fullName : undefined;
}

export function filterCommissions({
  commissions,
  parties,
  search,
  types,
}: {
  commissions: CommissionTransaction[];
  parties: Record<number, Party>;
  search: string;
  types: CommissionType[];
}): CommissionTransaction[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return commissions;

  return commissions.filter((commission) => {
    const client = getPartyName(parties, commission.clientPartyId);
    const referrer = getPartyName(parties, commission.referrerPartyId);
    return [
      commission.id,
      getTypeLabel(types, commission.type),
      commission.note,
      commission.agentId,
      client,
      referrer,
      commission.commissionAmount,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(needle);
  });
}
